import { localDevAddress, localDevServerAddress } from '../dev-server.js';

interface ServerConfiguration {
  storeId: string;
  isLocalEnv: boolean;
  serverUrl: string;
  devServerUrl: string;
  backupServerUrl: string;
}

const storeIdPattern = /^[A-Za-z0-9_-]+$/;
const storeHostPattern = /^([A-Za-z0-9_-]+)\.myconvermax\.com$/i;

const useDevServer = window.Convermax?.useDevServer;

const jsScriptDomain =
  window.document.currentScript instanceof HTMLScriptElement ? window.document.currentScript.src : undefined;

function isValidStoreId(value: unknown): value is string {
  return typeof value === 'string' && storeIdPattern.test(value);
}

function deriveServerConfiguration(): ServerConfiguration {
  const windowStoreId = isValidStoreId(window.Convermax?.config?.storeId)
    ? window.Convermax.config.storeId
    : isValidStoreId(window.__CONVERMAX__?.config?.storeId)
      ? window.__CONVERMAX__.config.storeId
      : undefined;

  if (!jsScriptDomain) {
    const storeId = windowStoreId ?? CONVERMAX_STORE_ID;
    const devServerUrl = `${localDevServerAddress}/${storeId}`;

    return {
      storeId,
      isLocalEnv: false,
      serverUrl: useDevServer ? devServerUrl : `https://${storeId}.myconvermax.com`,
      devServerUrl,
      backupServerUrl: useDevServer ? devServerUrl : `https://backup.convermax.com/${storeId}`,
    };
  }

  const scriptUrl = new URL(jsScriptDomain);
  const domainOrigin = scriptUrl.origin;
  const documentStoreId = scriptUrl.hostname.match(storeHostPattern)?.[1];
  const storeId = windowStoreId ?? documentStoreId ?? CONVERMAX_STORE_ID;

  if (!storeId) {
    throw new Error(
      'Unable to resolve Convermax store ID from runtime window config, the connected script hostname, or build metadata.',
    );
  }

  const isLocalEnv = domainOrigin === localDevAddress;
  const devServerUrl = `${localDevServerAddress}/${storeId}`;
  const backupServerUrl = useDevServer ? devServerUrl : `https://backup.convermax.com/${storeId}`;

  const serverUrl = useDevServer
    ? devServerUrl
    : documentStoreId === storeId
      ? domainOrigin
      : documentStoreId || isLocalEnv
        ? `https://${storeId}.myconvermax.com`
        : `https://client.convermax.com/${storeId}`;

  return {
    storeId,
    isLocalEnv,
    serverUrl,
    devServerUrl,
    backupServerUrl,
  };
}

const serverConfiguration = deriveServerConfiguration();

export const { storeId, isLocalEnv, serverUrl, backupServerUrl } = serverConfiguration;
export { jsScriptDomain, useDevServer };
export default serverConfiguration;
